/** Dashboard widget: risk rating for the user's primary portfolio. */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/api/client";
import type { Portfolio, PortfolioRiskAssessment } from "@/types";
import WidgetCard from "./WidgetCard";

const RATING_COLOR: Record<string, string> = {
  low: "text-signal-teal",
  moderate: "text-signal-amber",
  elevated: "text-signal-rose",
  high: "text-signal-rose",
};

export default function RiskSummaryWidget(): React.ReactElement {
  const [risk, setRisk] = useState<PortfolioRiskAssessment | null>(null);
  const [hasPortfolio, setHasPortfolio] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    apiClient
      .get<Portfolio[]>("/portfolios")
      .then(async ({ data }) => {
        if (data.length === 0) {
          setHasPortfolio(false);
          return;
        }
        const { data: riskData } = await apiClient.get<PortfolioRiskAssessment>(
          `/portfolios/${data[0].id}/risk`
        );
        setRisk(riskData);
      })
      .catch(() => setError("Unable to load risk summary"))
      .finally(() => setLoaded(true));
  }, []);

  return (
    <WidgetCard title="Risk summary" isLoading={!loaded} error={error}>
      {!hasPortfolio ? (
        <p className="text-sm text-mist-400">Create a portfolio to see its risk profile.</p>
      ) : risk ? (
        <>
          <p className={`text-xl font-semibold capitalize ${RATING_COLOR[risk.overall_rating]}`}>
            {risk.overall_rating}
          </p>
          <p className="text-xs text-mist-400">
            Concentration (HHI): {risk.concentration_hhi?.toFixed(0) ?? "—"}
          </p>
          <Link to="/risk" className="text-xs text-signal-teal hover:underline">
            View risk dashboard →
          </Link>
        </>
      ) : (
        <p className="text-sm text-mist-400">No risk data available yet.</p>
      )}
    </WidgetCard>
  );
}
